import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Clock, Coins } from 'lucide-react';

export default function TransactionReceiptPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const transaction = location.state?.transaction;

  // If no transaction data, redirect back
  if (!transaction) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Transaction not found</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Format number for display
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(Math.floor(num || 0));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get transaction status
  const getStatus = () => {
    const status = transaction.status || 'completed';
    if (status.toLowerCase() === 'completed' || status.toLowerCase() === 'success') {
      return { label: 'Transaction Successful', color: 'text-emerald-600', bgColor: 'bg-emerald-100', Icon: CheckCircle };
    }
    if (status.toLowerCase() === 'pending') {
      return { label: 'Transaction Pending', color: 'text-amber-600', bgColor: 'bg-amber-100', Icon: Clock };
    }
    return { label: 'Transaction Failed', color: 'text-red-600', bgColor: 'bg-red-100', Icon: XCircle };
  };

  // Get payment method display
  const getPaymentMethodDisplay = () => {
    const method = (transaction.payment_method || '').toUpperCase();
    if (method === 'POINTS') return 'Points';
    if (method === 'CARD') return 'Card';
    return transaction.payment_method || 'N/A';
  };

  const status = getStatus();
  const StatusIcon = status.Icon;

  // Extract transaction details - prioritize reference field from API response
  const transactionRef = transaction.reference || transaction.transaction_uuid || transaction.id || 'N/A';
  const rawCategory = transaction.category || transaction.metadata?.category || 'Purchase';
  const provider = transaction.metadata?.provider || transaction.provider || 'N/A';
  const rawProduct = transaction.metadata?.product || transaction.description || 'N/A';
  const recipient = transaction.metadata?.recipient || transaction.recipient || null;
  const totalAmount = transaction.amount || 0; // Fiat price of the product
  const pointsUsed = transaction.bold_credits || 0;
  const cashbackEarned = transaction.metadata?.cashback || 0;
  const paymentMethodUpper = (transaction.payment_method || '').toUpperCase();
  const isPointsPayment = paymentMethodUpper === 'POINTS';
  const isCardPayment = paymentMethodUpper === 'CARD';

  // Transform category for display (Mobile Pin -> Airtime)
  const isAirtimeTransaction = rawCategory.toLowerCase().includes('pin') ||
                               rawCategory.toLowerCase().includes('top up') ||
                               rawCategory.toLowerCase().includes('airtime');
  const category = isAirtimeTransaction ? 'Airtime' : rawCategory;

  // Transform product name for airtime (show "₦{amount} Airtime" instead of "10-50000 NGN PIN")
  const product = isAirtimeTransaction ? `₦${formatNumber(totalAmount)} Airtime` : rawProduct;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 bg-white z-10 px-4 py-3 flex items-center gap-3 border-b border-gray-100 pt-[calc(0.75rem+env(safe-area-inset-top))]">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={22} />
        </button>
        <h1 className="text-lg font-semibold text-gray-900">Transaction Receipt</h1>
      </header>

      <div className="p-4 max-w-lg mx-auto">
        {/* Hero Section */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-4">
          <div className="flex flex-col items-center text-center">
            <div className={`w-20 h-20 rounded-full ${status.bgColor} flex items-center justify-center mb-4`}>
              <StatusIcon size={40} className={status.color} />
            </div>
            <h2 className={`text-xl font-bold ${status.color}`}>{status.label}</h2>
            {/* Savings message for points transactions or card transactions with cashback */}
            {(isPointsPayment || cashbackEarned > 0) && (
              <div className="flex items-center gap-2 mt-3">
                <Coins size={18} className="text-amber-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                <p className="text-sm text-gray-600">
                  You saved <span className="font-semibold text-emerald-600">₦{formatNumber(isPointsPayment ? totalAmount : (cashbackEarned / 0.7407))}</span> on this transaction
                </p>
                <Coins size={18} className="text-amber-500 animate-bounce" style={{ animationDelay: '150ms' }} />
              </div>
            )}
          </div>
        </div>

        {/* Transaction Details */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-100">
            {/* Category */}
            <DetailRow label="Category" value={category} />

            {/* Provider */}
            <DetailRow label="Provider" value={provider} />

            {/* Product */}
            <DetailRow label="Product" value={product} />

            {/* Recipient (if available) */}
            {recipient && <DetailRow label="Recipient" value={recipient} />}

            {/* Product Amount - for points transactions */}
            {isPointsPayment && totalAmount > 0 && (
              <DetailRow label="Product Amount" value={`₦${formatNumber(totalAmount)}`} />
            )}

            {/* Amount Paid / Points Used */}
            {isCardPayment && (
              <DetailRow label="Amount Paid" value={`₦${formatNumber(totalAmount)}`} />
            )}
            {isPointsPayment && pointsUsed > 0 && (
              <DetailRow label="Total Pts Used" value={`${formatNumber(pointsUsed)} Pts`} />
            )}

            {/* Cashback Earned (if available) */}
            {cashbackEarned > 0 && (
              <DetailRow label="Cashback Earned" value={`${formatNumber(cashbackEarned)} Pts`} highlight />
            )}

            {/* Payment Method */}
            <DetailRow label="Payment Method" value={getPaymentMethodDisplay()} />

            {/* Date & Time */}
            <DetailRow label="Date & Time" value={formatDate(transaction.created_at)} />
          </div>

          {/* Transaction Reference Footer */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-400 text-center font-mono break-all">
              Ref: {transactionRef}
            </p>
          </div>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="w-full mt-6 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}

// Detail row component
function DetailRow({ label, value, mono = false, highlight = false }) {
  return (
    <div className="flex justify-between items-start px-4 py-3 gap-4">
      <span className="text-sm text-gray-500 flex-shrink-0">{label}</span>
      <span className={`text-sm font-medium text-right ${highlight ? 'text-emerald-600' : 'text-gray-900'} ${mono ? 'font-mono text-xs break-all' : ''}`}>
        {value}
      </span>
    </div>
  );
}
